/**
 * 63.4 step 8 (lane E makes them, lane F wires them): damaged and destroyed forms beyond the trees (rendering/
 * treeForms.ts) — the cracked rock, and a damaged pass any structure or vehicle model can take.
 *
 * - **The cracked rock** (for later: rocks stand for now, decision 130 (b)): the kit's rock split along a seeded vertical
 *   plane, its two sides parted and leaning apart over a dark seam, a few chips on the ground. Built as the stands build
 *   a whole rock (scale 1, no yaw, base at y = 0), so it swaps per instance the same way.
 * - **The damaged pass** (`damageModel`): the first of possibly several stages between whole and ruined (decision 130
 *   (a), "work towards partial destruction over time"), for the models that only had whole and a tipped-over ruin —
 *   the tower, the emplacement, the beacon, the supply crate, the vehicles. Scorch over its larger surfaces, its
 *   smallest parts knocked off as the level rises (aerials, lamps, rails), one high part knocked askew. Applied to a
 *   freshly built model before it is baked; deterministic in (model, seed, level).
 *
 * Presentation only.
 */
import * as THREE from 'three';
import { buildRock, type RockKind } from './biomeKit';
import { markShared } from './meshBake';
import { LYING_ROCKS } from '../constants';

const matCache = new Map<string, THREE.MeshLambertMaterial>();
function mat(color: string): THREE.MeshLambertMaterial {
  let m = matCache.get(color);
  if (!m) { m = markShared(new THREE.MeshLambertMaterial({ color, flatShading: true })); matCache.set(color, m); }
  return m;
}
function hash01(seed: number, i: number, salt: number): number {
  const h = Math.sin(seed * 12.9898 + i * 78.233 + salt * 37.719) * 43758.5453;
  return h - Math.floor(h);
}
function shade(hex: string, k: number): string { return '#' + new THREE.Color(hex).multiplyScalar(k).getHexString(); }

/** The forms' numbers. */
export const DAMAGE_FORM = {
  /** the damaged pass's default level (half health) */
  level: 0.5,
  scorch: '#3b3531',
  /** a scorch patch: its part's own colour times this (soot, not a black box) */
  soot: 0.45,
  /** the cracked rock: the gap between its sides and their lean, as shares of its size */
  crackGap: 0.06, crackLean: 0.12,
};

// ---------------------------------------------------------------------------
// The cracked rock
// ---------------------------------------------------------------------------

/**
 * A kit rock, cracked: split along a vertical plane through its middle (its bearing from the seed), the two sides parted
 * and leaning away from the seam, a dark seam between them, three chips at its foot. Scale 1, no yaw, base at y = 0.
 */
export function crackedRockModel(kind: RockKind, color: string, seed: number): THREE.Group {
  const rock = buildRock(kind, color, 1, seed);
  rock.rotation.set(0, 0, 0); rock.scale.setScalar(1);
  rock.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(rock, true), size = box.getSize(new THREE.Vector3()), c = box.getCenter(new THREE.Vector3());
  const a = hash01(seed, 0, 1) * Math.PI;
  const n = new THREE.Vector3(Math.cos(a), 0, Math.sin(a));                 // the crack's normal (horizontal)
  // (74.4: a long low band — the ledge — parts without tipping: its halves leaning would lift their far ends over the whole)
  const reach = Math.max(size.x, size.z) / 2, gap = DAMAGE_FORM.crackGap * reach * 2, lean = LYING_ROCKS.has(kind) ? 0 : DAMAGE_FORM.crackLean;
  const out = new THREE.Group();
  const meshes: THREE.Mesh[] = [];
  rock.traverse(o => { if ((o as THREE.Mesh).isMesh) meshes.push(o as THREE.Mesh); });
  /** One side of the crack: a group pivoting at the foot of the seam, pushed out along `sgn·n` and leaning away from it. */
  const side = (sgn: number) => {
    const g = new THREE.Group();
    g.position.set(c.x + n.x * sgn * gap / 2, 0, c.z + n.z * sgn * gap / 2);
    g.quaternion.setFromAxisAngle(new THREE.Vector3(-n.z, 0, n.x), -sgn * lean);   // tip away from the seam about the line along it
    out.add(g);
    return g;
  };
  const left = side(-1), right = side(1);
  const onSide = (m: THREE.Mesh) => { const p = new THREE.Box3().setFromObject(m, true).getCenter(new THREE.Vector3()); return (p.x - c.x) * n.x + (p.z - c.z) * n.z >= 0 ? 1 : -1; };
  const sides = meshes.map(onSide);
  const split = sides.some(s => s > 0) && sides.some(s => s < 0);
  for (let i = 0; i < meshes.length; i++) {
    const m = meshes[i];
    const into = (g: THREE.Group, shrink = 1, shift = 0) => {
      const copy = new THREE.Mesh(m.geometry, m.material);
      copy.applyMatrix4(m.matrixWorld);
      copy.position.sub(new THREE.Vector3(c.x, 0, c.z)).addScaledVector(n, shift);
      copy.scale.multiplyScalar(shrink);
      g.add(copy);
    };
    if (split) into(sides[i] > 0 ? right : left);
    else { into(right, 0.74, reach * 0.3); into(left, 0.74, -reach * 0.3); }   // one stone: two smaller halves either side of the seam
  }
  // each side rests on the ground: leaning out dips its far edge, so it is lifted by the dip
  for (const g of [left, right]) {
    out.updateMatrixWorld(true);
    const low = new THREE.Box3().setFromObject(g, true).min.y;
    if (low < 0) g.position.y -= low;
  }
  // the seam: a thin dark slab along the crack, most of the rock's height
  const seam = new THREE.Mesh(new THREE.BoxGeometry(gap * 0.6, size.y * 0.8, reach * 1.6), mat(shade(color, 0.35)));
  seam.position.set(c.x, size.y * 0.4, c.z);
  seam.rotation.y = -Math.atan2(n.z, n.x);
  out.add(seam);
  // three chips at its foot
  for (let k = 0; k < 3; k++) {
    const ang = a + Math.PI / 2 + (k - 1) * 0.7 + hash01(seed, k, 2) * 0.4, d = reach * (1.05 + hash01(seed, k, 3) * 0.4), s = reach * (0.1 + hash01(seed, k, 4) * 0.08);
    const chip = new THREE.Mesh(new THREE.IcosahedronGeometry(s, 0), mat(shade(color, 0.9 + hash01(seed, k, 5) * 0.2)));
    chip.position.set(c.x + Math.cos(ang) * d, s * 0.6, c.z + Math.sin(ang) * d);
    chip.rotation.set(hash01(seed, k, 6) * 3, hash01(seed, k, 7) * 3, 0);
    out.add(chip);
  }
  return out;
}

// ---------------------------------------------------------------------------
// The damaged pass, for any built model
// ---------------------------------------------------------------------------

const _ray = new THREE.Raycaster(), _from = new THREE.Vector3(), _dir = new THREE.Vector3();
/**
 * How deep under the point `p` (the root's frame, on the part's bounding face on `axis` (0 x, 2 z) at side `sign`) the
 * part's surface lies: a ray from just outside, inward along the axis, to its first hit within the part's half depth (the
 * root's units, from the face) — null where it meets nothing there (an opening).
 */
function surfaceDepth(mesh: THREE.Mesh, root: THREE.Object3D, p: THREE.Vector3, axis: 0 | 2, sign: number, size: THREE.Vector3): number | null {
  const k = root.matrixWorld.getMaxScaleOnAxis() || 1, lead = 0.05;
  _from.copy(p); _from.setComponent(axis, p.getComponent(axis) + sign * lead);
  _dir.set(0, 0, 0).setComponent(axis, -sign);
  _from.applyMatrix4(root.matrixWorld); _dir.transformDirection(root.matrixWorld);
  _ray.set(_from, _dir); _ray.far = (lead + (axis === 0 ? size.x : size.z) * 0.5) * k;
  const hit = _ray.intersectObject(mesh, false)[0];
  return hit ? hit.distance / k - lead : null;
}

/**
 * Damage a freshly built model in place to `level` (0 … 1): scorch patches over its larger surfaces (more with the level),
 * a share of its smallest parts knocked off (aerials, lamps, rails — never a part that holds the rest up), and its
 * highest small part knocked askew. Call before baking. `keep` lists parts that must stay (a seat's anchor, a gun's
 * muzzle). Returns how many parts it knocked off.
 */
export function damageModel(root: THREE.Object3D, seed: number, level: number = DAMAGE_FORM.level, keep: ReadonlyArray<THREE.Object3D> = []): number {
  const k = Math.max(0, Math.min(1, level));
  root.updateMatrixWorld(true);
  const inv = new THREE.Matrix4().copy(root.matrixWorld).invert();
  const parts: Array<{ mesh: THREE.Mesh; box: THREE.Box3; vol: number; childless: boolean }> = [];
  const kept = new Set<THREE.Object3D>();
  for (const o of keep) o.traverse(x => kept.add(x));
  root.traverse(o => {
    const m = o as THREE.Mesh;
    if (!m.isMesh || !m.visible || kept.has(m)) return;
    const box = new THREE.Box3().setFromObject(m, true).applyMatrix4(inv), s = box.getSize(new THREE.Vector3());
    parts.push({ mesh: m, box, vol: s.x * s.y * s.z, childless: m.children.length === 0 });
  });
  if (!parts.length) return 0;
  const whole = new THREE.Box3(); for (const p of parts) whole.union(p.box);
  const wsize = whole.getSize(new THREE.Vector3()), wvol = Math.max(1e-12, wsize.x * wsize.y * wsize.z);
  // knocked off: the small parts (each under 0.5 % of the bounds), a share growing with the level
  const small = parts.filter(p => p.childless && p.vol < wvol * 0.005).map((p, i) => ({ p, h: hash01(seed, i, 21) })).sort((a, b) => a.h - b.h);
  const off = Math.floor(small.length * 0.45 * k);
  for (let i = 0; i < off; i++) small[i].p.mesh.parent?.remove(small[i].p.mesh);
  // knocked askew: the highest remaining small part, its ends swung at most a fiftieth of the model (a long one, a door's jamb
  // or lintel, turned the full angle about its middle leaned into the opening it framed — a part in an opening, as the black
  // patch was; the coordinator, Oct 28)
  const high = small.slice(off).map(x => x.p).sort((a, b) => b.box.max.y - a.box.max.y)[0];
  if (high && k > 0) {
    const hs = high.box.getSize(new THREE.Vector3()), reach = Math.max(hs.x, hs.y, hs.z) / 2, cap = (0.02 * Math.max(wsize.x, wsize.y, wsize.z)) / Math.max(1e-6, reach);
    high.mesh.rotation.z += Math.min(0.5 * k, cap) * (hash01(seed, 1, 22) < 0.5 ? -1 : 1); high.mesh.rotation.x += Math.min(0.25 * k, cap);
  }
  // scorch: soot patches on the faces of the larger parts (each part's own colour darkened), a few more with the level. Each
  // lies on its face, as wide as that face at most, keyed along it — never across an opening in it: every corner and its
  // middle must meet the part (fix, the owner, Oct 28: the patch sat centred on a wall's sheathing in a near-black — over a
  // window's opening, "a black rectangle … blocks the view" — and on a thin wall's end it stood out as a fin a wall's width)
  const big = parts.filter(p => p.vol >= wvol * 0.005 && p.mesh.parent).map((p, i) => ({ p, h: hash01(seed, i, 23) })).sort((a, b) => a.h - b.h);
  const n = Math.min(big.length, 1 + Math.round(5 * k));
  const t = Math.max(wsize.x, wsize.y, wsize.z) * 0.006;
  for (let i = 0; i < n; i++) {
    const { mesh, box: b } = big[i].p, s = b.getSize(new THREE.Vector3());
    const face = Math.floor(hash01(seed, i, 24) * 4);   // which side face: +x, −x, +z, −z
    const across = face < 2 ? s.z : s.x;                // the face's width
    const w = across * (0.15 + 0.2 * hash01(seed, i, 25)), h = s.y * (0.15 + 0.25 * hash01(seed, i, 26));   // a patch, never the whole face
    const m0 = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material;
    const color = shade('#' + ((m0 as THREE.MeshLambertMaterial).color ?? new THREE.Color(DAMAGE_FORM.scorch)).getHexString(), DAMAGE_FORM.soot);
    for (let tries = 0; tries < 4; tries++) {
      const y = b.min.y + h / 2 + (s.y - h) * hash01(seed, i, 27 + 10 * tries);
      const a = (face < 2 ? b.min.z : b.min.x) + w / 2 + (across - w) * hash01(seed, i, 28 + 10 * tries);
      const out = face === 0 ? b.max.x : face === 1 ? b.min.x : face === 2 ? b.max.z : b.min.z, sign = face % 2 === 0 ? 1 : -1, axis = face < 2 ? 0 : 2;
      const at = (da: number, dy: number) => (face < 2 ? new THREE.Vector3(out, y + dy, a + da) : new THREE.Vector3(a + da, y + dy, out));
      // its middle and a grid over it each meet the part (none over an opening), at about one depth (a face, not a turned
      // edge); the grid finer than an opening (its corners alone missed a slit crossing the patch between them)
      const step = Math.max(wsize.x, wsize.y, wsize.z) * 0.06, nu = Math.max(3, Math.ceil(w / step) + 1), nv = Math.max(3, Math.ceil(h / step) + 1);
      const grid: Array<[number, number]> = [[0, 0]];
      for (let iu = 0; iu < nu; iu++) for (let iv = 0; iv < nv; iv++) grid.push([-0.45 + 0.9 * iu / (nu - 1), -0.45 + 0.9 * iv / (nv - 1)]);
      const depths = grid.map(([u, v]) => surfaceDepth(mesh, root, at(u * w, v * h), axis, sign, s));
      const d0 = depths[0];
      if (d0 === null || depths.some(d => d === null || Math.abs(d - d0) > Math.max(w, h) * 0.3 + 2 * t)) continue;
      const patch = new THREE.Mesh(new THREE.BoxGeometry(face < 2 ? t : w, h, face < 2 ? w : t), mat(color));
      patch.position.copy(at(0, 0)).setComponent(axis, out - sign * (d0 - t / 2));   // (on the surface its middle meets)
      root.add(patch);                        // the boxes are in the root's own frame
      break;
    }
  }
  return off;
}
